import Raven from 'raven-js';

import { urls } from '~/preloaded';
import settings from '~/settings';
import { CLAN_MANAGEMENT_STATUSES, CLAN_RENAMING_STATUSES } from '~/api/const';
import { ATTRIBUTES_VALIDATION_STATE } from '~/constants';
import { get, post } from '~/helpers/api';
import { getShiftedIsoString } from '~/helpers/datetime';
import { delay } from '~/helpers/delay';
import { t } from '~/helpers/localization';
import { isPreModerateNameTag } from '~/helpers/moderation';
import { ROLE_NAMES } from '~/roles';
import { currentClanSelector } from '~/store/selectors/currentAccountSelector';
import { currentAccountActions, type ICurrentAccountState } from '~/store/slices/currentAccountSlice';

import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';
import { promiseWithGlobalSpinner } from '~/store/utils/globalSpinner';
import {
  sendClanRenameCompleteNotification,
  sendClanRenameErrorNotification,
  sendClanRenamePreModerationNotification,
} from '~/web2ClientAPI/notifications';
import { shouldHideDialogOnError } from '~/Actions/ActionClanEdit';
import { actionsClanProfile } from '~/Actions/ActionClanProfile';

import type { FinishCreationType } from '~/Actions/ActionClanCreate';
import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { InferActionsType } from '~/Reducers';
import type { IClanCreateStateFields } from '~/Reducers/ReducerClanCreate';
import type { IClanRenameStateFields } from '~/Reducers/ReducerClanRename';
import type { IClanDisplay } from '~/Reducers/ReducerSupply';
import type { AppAsyncThunk, IAppDispatch } from '~/store';
import type {
  IApiClanRename,
  IApiClanRenameCheckStatus,
  IApiClanRenameSingleCheckStatus,
  IApiError,
} from '~/types/api';
import type { ClanPreloaded } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

export const ERROR_RENAMING_CLAN = 'ERROR_RENAMING_CLAN';
export const FINISH_CHECK_AVAILABILITY_CLAN_RENAMING = 'FINISH_CHECK_AVAILABILITY_CLAN_RENAMING';
export const FINISH_CHECK_CLAN_RENAMING_STATUS = 'FINISH_CHECK_CLAN_RENAMING_STATUS';
export const SET_INITIAL_STATE_RENAMING_CLAN = 'SET_INITIAL_STATE_RENAMING_CLAN';
export const SET_STATUS_URL_RENAMING_CLAN = 'SET_STATUS_URL_RENAMING_CLAN';
export const START_CHECK_AVAILABILITY_CLAN_RENAMING = 'START_CHECK_AVAILABILITY_CLAN_RENAMING';
export const START_CHECK_CLAN_RENAMING_STATUS = 'START_CHECK_CLAN_RENAMING_STATUS';
export const START_RENAMING_CLAN = 'START_RENAMING_CLAN';
export const STOP_RENAMING_CLAN = 'STOP_RENAMING_CLAN';
export const UPDATE_RENAMING_CLAN_FIELD_VALUE = 'UPDATE_RENAMING_CLAN_FIELD_VALUE';

export type ActionsType = InferActionsType<typeof actionsClanRename>;
export type IClanRenameErrorsCommon = Nullable<{
  errors: {
    [key in keyof IClanRenameStateFields]?: string[];
  };
}>;

export const actionsClanRename = {
  startRenamingClan: () =>
    ({
      type: START_RENAMING_CLAN,
    }) as const,

  stopRenamingClan: () =>
    ({
      type: STOP_RENAMING_CLAN,
    }) as const,

  errorRenamingClan: (error: IApiError) =>
    ({
      type: ERROR_RENAMING_CLAN,
      error,
    }) as const,

  setStatusUrlRenamingClan: (url: string) =>
    ({
      type: SET_STATUS_URL_RENAMING_CLAN,
      url,
    }) as const,

  startCheckAvailabilityRenamingClan: (field: keyof IClanRenameStateFields) =>
    ({
      type: START_CHECK_AVAILABILITY_CLAN_RENAMING,
      field,
    }) as const,

  finishCheckAvailabilityRenamingClan: (data: FinishCreationType) =>
    ({
      type: FINISH_CHECK_AVAILABILITY_CLAN_RENAMING,
      data,
    }) as const,

  setInitialStateRenamingClan: () =>
    ({
      type: SET_INITIAL_STATE_RENAMING_CLAN,
    }) as const,

  startCheckingRenamingClanStatus: () =>
    ({
      type: START_CHECK_CLAN_RENAMING_STATUS,
    }) as const,

  finishCheckingRenamingClanStatus: () =>
    ({
      type: FINISH_CHECK_CLAN_RENAMING_STATUS,
    }) as const,

  updateFieldValueRenamingClan: (field: keyof IClanRenameStateFields, value: string, error: IApiError) =>
    ({
      type: UPDATE_RENAMING_CLAN_FIELD_VALUE,
      field,
      value,
      error,
    }) as const,
};

const processExcludedFromClanError = (
  dispatch: IAppDispatch,
  sendErrorNotification: typeof sendClanRenameErrorNotification,
  clan?: IClanDisplay,
) => {
  sendErrorNotification('excluded_from_clan', clan);

  const promise = dispatch(syncAccountInfoThunk());
  void promiseWithGlobalSpinner(dispatch, promise);
};

const processError = (
  dispatch: IAppDispatch,
  error: {
    additional_info?: {
      reason?: string;
      role?: ROLE_NAMES;
      field?: keyof IClanCreateStateFields;
      clan?: IClanDisplay;
    };
    error?: string;
    error_code?: string;
  },
  sendErrorNotification: typeof sendClanRenameErrorNotification,
) => {
  const reason = error?.additional_info?.reason;

  if (reason === 'insufficient_permissions') {
    const role = error?.additional_info?.role ?? null;
    if (role) {
      dispatch(currentAccountActions.updateAccountInfo({ roleName: role }));
      sendErrorNotification(reason);
    } else {
      processExcludedFromClanError(dispatch, sendErrorNotification, error?.additional_info?.clan);
    }
  } else if (reason === 'excluded_from_clan') {
    processExcludedFromClanError(dispatch, sendErrorNotification, error?.additional_info?.clan);
  } else if (reason === 'clan_already_exist') {
    sendErrorNotification('clan_already_exist');
    // TODO WGCP-3104 add to response reason not_enought_gold
  } else if (error?.error === 'NO_GOLD') {
    sendErrorNotification('no_gold');
  } else if (reason === 'rename_in_cooldown') {
    sendErrorNotification(reason);
  } else if (error?.error_code === 'restriction_error') {
    sendErrorNotification('restriction_error', '');
  } else if (reason === 'err_bad_words') {
    const messages = {
      name: t('Название содержит запрещенные слова.'),
      tag: t('Тег содержит запрещенные слова.'),
    };

    const field = error?.additional_info?.field;
    if (field) {
      dispatch(
        actionsClanRename.finishCheckAvailabilityRenamingClan({
          field: field,
          error: messages[field],
          available: ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE,
        }),
      );
    }
  } else {
    sendErrorNotification();
  }
};

const checkRenamingClanStatus = async (url: string, start: number): Promise<CLAN_MANAGEMENT_STATUSES> => {
  await delay(settings.ajaxTools.pendingOperationWait);

  try {
    const response = await get<IApiClanRenameCheckStatus>(url);

    if (response?.error) {
      throw new Error(response.error);
    }

    const model = response?._meta_?.model;

    if (model && response[model].status === CLAN_MANAGEMENT_STATUSES.PROCESSING) {
      if (start + settings.ajaxTools.pendingOperationTimeout < Date.now()) {
        return CLAN_MANAGEMENT_STATUSES.TIMEOUT;
      }

      return await checkRenamingClanStatus(url, start);
    }

    return response[model].status;
  } catch (e) {
    return CLAN_MANAGEMENT_STATUSES.ERROR;
  }
};

export const singleCheckRenamingClanStatus = (): AppAsyncThunk => async (dispatch, getState) => {
  const state = getState();
  const url = state.clanRename.statusUrl;

  if (!url) {
    return;
  }

  try {
    const response = await get<IApiClanRenameSingleCheckStatus>(url);

    const model = response?._meta_?.model;

    if (model && response[model].status === CLAN_RENAMING_STATUSES.OK) {
      const state = getState();
      const account = state.currentAccount;
      const currentClan = currentClanSelector(state);

      if (!currentClan) {
        return;
      }

      const data = {
        tag: state.clanRename.tag.value || currentClan.tag,
        name: state.clanRename.name.value || currentClan.name,
      };

      processSuccessRenameClan(dispatch, account, data, currentClan);
    }
  } catch (error) {
    console.error('Failed to check single renaming clan status', error);
  }
};

const processSuccessRenameClan = (
  dispatch: IAppDispatch,
  account: ICurrentAccountState,
  tagAndNameData: Pick<IClanDisplay, 'tag' | 'name'>,
  currentClan: ClanInfoType | ClanPreloaded,
) => {
  if (!account.clanId) {
    return;
  }

  const hasFreeRenameAccess = account.isFreeRenameAvailable && account.roleName === ROLE_NAMES.COMMANDER;
  const renameCost = hasFreeRenameAccess ? 0 : settings.clanRename.cost;
  const accountInfoToUpdate = {
    isFreeRenameAvailable: renameCost ? account.isFreeRenameAvailable : false,
    nextRenameAvailableAt: getShiftedIsoString(settings.clanRename.renameCooldown),
  };

  if (isPreModerateNameTag) {
    sendClanRenamePreModerationNotification();
  } else {
    sendClanRenameCompleteNotification(currentClan, tagAndNameData, renameCost);
  }

  dispatch(actionsClanRename.errorRenamingClan(null));
  dispatch(currentAccountActions.updateAccountInfo(accountInfoToUpdate));

  const clanData = Object.assign({ clanId: account.clanId }, tagAndNameData);

  dispatch(actionsClanProfile.changeClanAttributesSuccessed(clanData));
  dispatch(actionsClanRename.setInitialStateRenamingClan());
};

export const renameClan =
  ({ signal }: { signal?: AbortSignal } = {}): AppAsyncThunk<boolean> =>
  async (dispatch, getState) => {
    const state = getState();

    if (state.clanRename.isProcessing) {
      const msg = 'Clan in renaming process';
      console.error(msg);

      if (settings.sentryUrl) {
        Raven.captureMessage(msg, { extra: state.clanRename });
      }
      return false;
    }

    const { tag, name } = state.clanRename;
    const availableState = ATTRIBUTES_VALIDATION_STATE.AVAILABLE;

    if (tag.available !== availableState || name.available !== availableState) {
      console.error('Tag or name is not available');
      return false;
    }

    const account = state.currentAccount;
    const currentClan = currentClanSelector(state);

    if (!currentClan) {
      return false;
    }

    dispatch(actionsClanRename.startRenamingClan());

    try {
      const url = urls.renameClan;
      const payload = {
        tag: tag.value || currentClan.tag,
        name: name.value || currentClan.name,
        free: account.isFreeRenameAvailable,
      };

      const response = await post<IApiClanRename>(url, payload, signal && { signal });

      let status = response.status;

      if (status === CLAN_MANAGEMENT_STATUSES.PROCESSING) {
        dispatch(actionsClanRename.startCheckingRenamingClanStatus());
        dispatch(actionsClanRename.setStatusUrlRenamingClan(response.status_url));

        status = await checkRenamingClanStatus(response.status_url, Date.now());
      }

      if (status === CLAN_MANAGEMENT_STATUSES.ERROR || response?.errors || !!response?.error) {
        processError(dispatch, response, sendClanRenameErrorNotification);
        dispatch(actionsClanRename.errorRenamingClan(response));

        return shouldHideDialogOnError(response);
      } else if (status === CLAN_MANAGEMENT_STATUSES.TIMEOUT) {
        dispatch(actionsClanRename.finishCheckingRenamingClanStatus());
      } else {
        processSuccessRenameClan(dispatch, account, payload, currentClan);
      }
      return true;
    } catch (e) {
      sendClanRenameErrorNotification();
      dispatch(
        actionsClanRename.errorRenamingClan({
          error: t('Произошла ошибка. Повторите попытку позже'),
        }),
      );
      return false;
    }
  };
